import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, fmtDate, fmtUsd } from '../api';
import { useAuth } from '../auth';
import { Badge, Button, Card, Empty, ErrorBox, Field, Flash, inputCls, Loading, Markdown, PageHeader, StateBadge, Table, useAction, useApi } from '../components/ui';

const PILLARS = [
  ['Autoridade técnica', 30],
  ['Impactos e riscos de negócio', 20],
  ['Cases, bastidores e provas', 15],
  ['Inteligência e tendências', 15],
  ['Cultura AGIR e pessoas', 10],
  ['Eventos e relacionamento', 10],
] as const;

export function Cockpit() {
  const { can } = useAuth();
  const plans = useApi<any[]>('/api/plans');
  const reports = useApi<any[]>(can('analytics:read') ? '/api/reports?kind=weekly' : null);
  const status = useApi<any[]>(can('analytics:read') ? '/api/reports?kind=status_daily' : null);
  const act = useAction();
  const plan = plans.data?.[0];
  return (
    <>
      <PageHeader
        title="CMO Cockpit"
        subtitle="Plano semanal (W03), OKRs como hipóteses mensuráveis, riscos e relatórios executivos."
        actions={
          <>
            {can('plan:confirm') && (
              <Button disabled={act.busy} onClick={() => act.run(() => api('/api/plans/run', { method: 'POST' }), 'Plano solicitado ao CMO AI (A01). Atualize em instantes.').then(() => setTimeout(plans.reload, 1500))}>
                Gerar plano semanal agora
              </Button>
            )}
            {can('analytics:read') && (
              <Button variant="secondary" disabled={act.busy} onClick={() => act.run(() => api('/api/reports/weekly/run', { method: 'POST' }), 'Relatório solicitado ao A19.').then(() => setTimeout(reports.reload, 1500))}>
                Gerar relatório semanal
              </Button>
            )}
          </>
        }
      />
      <ErrorBox error={act.error} />
      <Flash message={act.message} />
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="OKRs do trimestre (hipóteses — sem linha-base)" className="lg:col-span-2">
          <ul className="space-y-2 text-sm">
            <li>
              <strong>O1 — Autoridade técnica junto ao ICP.</strong> KR: cadência de 3 posts/semana aprovados no SLA; KR: linha-base de engajamento qualificado por vertical em 4 semanas{' '}
              <Badge tone="amber">hipótese</Badge>
            </li>
            <li>
              <strong>O2 — Demanda rastreável.</strong> KR: 100% das peças com UTM; KR: reuniões de diagnóstico registradas no CRM <Badge tone="slate">CRM indefinido</Badge>
            </li>
            <li>
              <strong>O3 — Operação confiável.</strong> KR: 0 publicações sem aprovação válida; KR: custo de IA dentro do teto mensal
            </li>
          </ul>
          <p className="mt-2 text-xs text-slate-600">Metas numéricas serão definidas após coleta de linha-base (GA4/LinkedIn/CRM ainda não conectados).</p>
        </Card>
        <Card title="Pilares editoriais">
          <ul className="space-y-1 text-sm">
            {PILLARS.map(([p, w]) => (
              <li key={p} className="flex items-center gap-2">
                <div className="h-2 rounded bg-brand-600" style={{ width: `${w * 2}px` }} aria-hidden />
                <span>
                  {p} — {w}%
                </span>
              </li>
            ))}
          </ul>
        </Card>
        <Card title={`Sprint semanal ${plan ? `(${fmtDate(plan.periodStart, false)})` : ''}`} className="lg:col-span-3" actions={plan && <StateBadge state={plan.status} />}>
          {plans.loading && !plans.data ? (
            <Loading />
          ) : plan ? (
            <>
              <Table head={['Item', 'Pilar', 'Canal', 'Dono', 'Dia', 'Dependências', 'Racional']}>
                {plan.data.items.map((i: any, idx: number) => (
                  <tr key={idx}>
                    <td className="px-3 py-2">{i.title}</td>
                    <td className="px-3 py-2 text-xs">{i.pillar}</td>
                    <td className="px-3 py-2 text-xs">{i.channel}</td>
                    <td className="px-3 py-2">{i.ownerAgent}</td>
                    <td className="px-3 py-2">{i.dueDay}</td>
                    <td className="px-3 py-2 text-xs">{i.dependsOn.join(', ')}</td>
                    <td className="px-3 py-2 text-xs">{i.rationale}</td>
                  </tr>
                ))}
              </Table>
              <div className="mt-3 grid gap-3 md:grid-cols-3 text-sm">
                <div>
                  <h3 className="text-xs font-semibold uppercase text-slate-500">Riscos</h3>
                  <ul className="list-disc pl-5">{plan.data.risks.map((r: string) => <li key={r}>{r}</li>)}</ul>
                </div>
                <div>
                  <h3 className="text-xs font-semibold uppercase text-slate-500">Recomendações de parada</h3>
                  {plan.data.stopRecommendations.length ? <ul className="list-disc pl-5">{plan.data.stopRecommendations.map((r: string) => <li key={r}>{r}</li>)}</ul> : <p className="text-slate-500">Nenhuma.</p>}
                </div>
                <div>
                  <h3 className="text-xs font-semibold uppercase text-slate-500">Decisões para o CEO</h3>
                  <ul className="list-disc pl-5">{plan.data.decisionsNeeded.map((r: string) => <li key={r}>{r}</li>)}</ul>
                </div>
              </div>
              {plan.status === 'PROPOSED' && can('plan:confirm') && (
                <div className="mt-3 flex gap-2">
                  <Button variant="success" disabled={act.busy} onClick={() => act.run(() => api(`/api/plans/${plan.id}/decision`, { method: 'POST', body: { decision: 'CONFIRMED' } }), 'Sprint confirmado: demandas abertas e agentes trabalhando. As peças chegam para sua aprovação.').then(plans.reload)}>
                    Confirmar e iniciar produção
                  </Button>
                  <Button variant="secondary" disabled={act.busy} onClick={() => act.run(() => api(`/api/plans/${plan.id}/decision`, { method: 'POST', body: { decision: 'REJECTED' } }), 'Plano rejeitado.').then(plans.reload)}>
                    Rejeitar
                  </Button>
                </div>
              )}
              {plan.status === 'CONFIRMED' && can('plan:confirm') && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button variant="success" disabled={act.busy} onClick={() => act.run(() => api(`/api/plans/${plan.id}/open-requests`, { method: 'POST' }), 'Produção iniciada: as peças que faltavam viraram demandas e os agentes estão trabalhando.').then(plans.reload)}>
                    Iniciar produção das peças
                  </Button>
                  <span className="text-xs text-slate-600">Abre as demandas das peças ainda não abertas (não duplica as já abertas).</span>
                </div>
              )}
            </>
          ) : (
            <Empty>Nenhum plano ainda. O CMO AI propõe toda segunda às 09:00 ou sob demanda.</Empty>
          )}
        </Card>
        {can('analytics:read') && (
          <>
            <Card title="Último relatório executivo (A19)" className="lg:col-span-2">
              {reports.data?.[0] ? <Markdown text={reports.data[0].markdown} /> : <Empty>Nenhum relatório. Gera sexta às 17:00 ou sob demanda.</Empty>}
            </Card>
            <Card title="Status diário (09:00)">{status.data?.[0] ? <Markdown text={status.data[0].markdown} /> : <Empty>Ainda não gerado.</Empty>}</Card>
          </>
        )}
      </div>
    </>
  );
}

export function Agents() {
  const { data, error, loading } = useApi<any[]>('/api/agents');
  const wf = useApi<any[]>('/api/workflows');
  return (
    <>
      <PageHeader title="Agentes" subtitle="20 agentes lógicos. Inativos aparecem como planejados e não executam." />
      <ErrorBox error={error} />
      {loading && !data ? (
        <Loading />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data?.map((a) => (
            <Link key={a.id} to={`/agentes/${a.id}`} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:border-brand-500">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">
                  {a.id} · {a.name}
                </span>
                <StateBadge state={a.status} />
              </div>
              <p className="mt-1 line-clamp-3 text-xs text-slate-600">{a.config.mission}</p>
              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-600">
                <Badge>{a.phase}</Badge>
                <Badge>{a.model}</Badge>
                <span>v{a.version}</span>
                <span>· execuções: {a.stats?.runs ?? 0}</span>
                <span>· falhas: {a.stats?.failures ?? 0}</span>
                <span>· custo: {fmtUsd(a.stats?.cost ?? 0)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
      <Card title="Workflows W01–W14" className="mt-6">
        <Table head={['ID', 'Workflow', 'Status', 'Gatilho']}>
          {wf.data?.map((w) => (
            <tr key={w.id}>
              <td className="px-3 py-2 font-mono">{w.id}</td>
              <td className="px-3 py-2">{w.name}</td>
              <td className="px-3 py-2">
                <StateBadge state={w.status} />
              </td>
              <td className="px-3 py-2 text-xs">{w.trigger}</td>
            </tr>
          ))}
        </Table>
      </Card>
    </>
  );
}

export function AgentDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const { data, error, loading, reload } = useApi<any>(`/api/agents/${id}`, [id]);
  const act = useAction();
  const [prompt, setPrompt] = useState<string | null>(null);
  const [budget, setBudget] = useState<string>('');
  const [note, setNote] = useState('');
  if (loading && !data) return <Loading />;
  if (!data) return <ErrorBox error={error} />;
  const d = data.definition;
  const c = d.config;
  const list = (title: string, items: string[]) => (
    <div>
      <h3 className="text-xs font-semibold uppercase text-slate-500">{title}</h3>
      <ul className="list-disc pl-5 text-sm">{items.map((x) => <li key={x}>{x}</li>)}</ul>
    </div>
  );
  return (
    <>
      <PageHeader title={`${d.id} · ${d.name}`} subtitle={<span className="flex gap-2"><StateBadge state={d.status} /> v{d.version} · {d.model} · superior: {c.superior}</span>} />
      <ErrorBox error={act.error} />
      <Flash message={act.message} />
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Mandato" className="lg:col-span-2">
          <p className="mb-3 text-sm">{c.mission}</p>
          <div className="grid gap-3 md:grid-cols-2">
            {list('Autoridade', c.authority)}
            {list('Limites', c.limits)}
            {list('Antiobjetivos', c.anti_objectives)}
            {list('Condições de bloqueio', c.blocking_conditions)}
            {list('SOP', c.sop)}
            {list('Checklist antifalhas', c.checklist)}
            {list('Critérios de aceitação', c.acceptance_criteria)}
            {list('KPIs', c.kpis)}
          </div>
        </Card>
        <Card title="Operação">
          <dl className="space-y-2 text-sm">
            <div><dt className="text-xs font-semibold uppercase text-slate-500">Execução</dt><dd>{c.execution}</dd></div>
            <div><dt className="text-xs font-semibold uppercase text-slate-500">Gatilhos</dt><dd>manual: {String(c.triggers.manual)} · agenda: {c.triggers.schedule ?? '—'} · eventos: {c.triggers.events.join(', ') || '—'}</dd></div>
            <div><dt className="text-xs font-semibold uppercase text-slate-500">Ferramentas permitidas</dt><dd>{c.tools.length ? c.tools.join(', ') : 'nenhuma'}</dd></div>
            <div><dt className="text-xs font-semibold uppercase text-slate-500">Fontes de dados</dt><dd>{c.data_sources.join('; ')}</dd></div>
            <div><dt className="text-xs font-semibold uppercase text-slate-500">Credenciais</dt><dd>{c.credentials.join('; ') || '—'}</dd></div>
            <div><dt className="text-xs font-semibold uppercase text-slate-500">Orçamento</dt><dd>US$ {d.monthlyBudgetUsd}/mês · timeout {c.budget.timeout_s}s · {c.budget.max_tokens} tokens · {c.budget.max_turns} turnos</dd></div>
            <div><dt className="text-xs font-semibold uppercase text-slate-500">Aprovação humana</dt><dd>{c.human_approval}</dd></div>
            <div><dt className="text-xs font-semibold uppercase text-slate-500">Custo por mês</dt><dd>{data.costByPeriod.length ? data.costByPeriod.map((p: any) => `${p.period}: ${fmtUsd(p.total)}${p.estimate ? ' (estimado)' : ''}`).join(' · ') : '—'}</dd></div>
            <div><dt className="text-xs font-semibold uppercase text-slate-500">Testes</dt><dd className="text-xs">{c.tests.join('; ')}</dd></div>
          </dl>
        </Card>
        <Card title="Prompt especializado" className="lg:col-span-2">
          {prompt === null ? (
            <>
              <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-3 text-xs">{d.prompt}</pre>
              <p className="mt-2 text-xs text-slate-500">O runtime acrescenta um preâmbulo fixo de segurança não editável (isolamento de fontes externas, proibição de publicar/aprovar/gastar).</p>
              {can('agent:configure') && (
                <Button variant="secondary" onClick={() => { setPrompt(d.prompt); setBudget(String(d.monthlyBudgetUsd)); }}>
                  Editar (gera nova versão)
                </Button>
              )}
            </>
          ) : (
            <div className="space-y-2">
              <Field label="Prompt">
                <textarea className={`${inputCls} font-mono text-xs`} rows={16} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
              </Field>
              <Field label="Orçamento mensal (US$)">
                <input className={inputCls} type="number" min="0" step="0.5" value={budget} onChange={(e) => setBudget(e.target.value)} />
              </Field>
              <Field label="Nota da alteração" required>
                <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
              <div className="flex gap-2">
                <Button disabled={act.busy || note.length < 5} onClick={() => act.run(() => api(`/api/agents/${d.id}`, { method: 'PUT', body: { prompt, monthlyBudgetUsd: Number(budget), changeNote: note } }), 'Nova versão salva.').then((ok) => { if (ok) setPrompt(null); return reload(); })}>
                  Salvar versão
                </Button>
                <Button variant="secondary" onClick={() => setPrompt(null)}>Cancelar</Button>
              </div>
            </div>
          )}
        </Card>
        <Card title="Versões (rollback)">
          <ul className="space-y-1 text-xs">
            {data.versions.map((v: any) => (
              <li key={v.version} className="flex items-center justify-between gap-2">
                <span>v{v.version} · {v.changedBy} · {fmtDate(v.createdAt)} — {v.changeNote}</span>
                {can('agent:configure') && v.version !== d.version && (
                  <button className="text-brand-700 underline" onClick={() => act.run(() => api(`/api/agents/${d.id}/rollback`, { method: 'POST', body: { version: v.version } }), `Rollback para v${v.version}.`).then(reload)}>
                    restaurar
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Card>
        <Card title="Últimas execuções" className="lg:col-span-3">
          <Table head={['Run', 'Status', 'Provedor', 'Tokens', 'Custo', 'Duração', 'Início', 'Erro']} empty={!data.runs.length}>
            {data.runs.map((r: any) => (
              <tr key={r.id}>
                <td className="px-3 py-1 font-mono text-[11px]">{r.id}</td>
                <td className="px-3 py-1">{r.status}</td>
                <td className="px-3 py-1">{r.provider}</td>
                <td className="px-3 py-1 text-xs">{r.tokensIn}/{r.tokensOut}</td>
                <td className="px-3 py-1 text-xs">{fmtUsd(r.costUsd)}{r.costIsEstimate ? '*' : ''}</td>
                <td className="px-3 py-1 text-xs">{r.durationMs ?? '—'} ms</td>
                <td className="px-3 py-1 text-xs">{fmtDate(r.startedAt)}</td>
                <td className="px-3 py-1 text-xs text-red-800">{r.error}</td>
              </tr>
            ))}
          </Table>
          <p className="mt-1 text-[11px] text-slate-500">* custo estimado (provedor simulado).</p>
        </Card>
      </div>
    </>
  );
}

export function Radar() {
  const { can } = useAuth();
  const { data, error, loading, reload } = useApi<any>('/api/radar');
  const reps = useApi<any[]>(can('analytics:read') ? '/api/reports?kind=radar_daily' : null);
  const act = useAction();
  const [topic, setTopic] = useState('');
  const items = (data?.items ?? []).filter((i: any) => !topic || i.topic === topic);
  const topics = [...new Set((data?.items ?? []).map((i: any) => i.topic).filter(Boolean))] as string[];
  return (
    <>
      <PageHeader
        title="Radar de Mercado"
        subtitle="W02: fontes autorizadas, dedupe, detecção de prompt injection e classificação pelo A03. Conteúdo externo é tratado como dado não confiável."
        actions={can('radar:run') && <Button disabled={act.busy} onClick={() => act.run(() => api('/api/radar/run', { method: 'POST' }), 'Radar enfileirado; atualize em alguns segundos.').then(() => setTimeout(() => { reload(); reps.reload(); }, 4000))}>Executar radar agora</Button>}
      />
      <ErrorBox error={act.error ?? error} />
      <Flash message={act.message} />
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Itens" className="lg:col-span-2" actions={<select aria-label="Filtrar tema" className="rounded border border-slate-300 px-2 py-1 text-xs" value={topic} onChange={(e) => setTopic(e.target.value)}><option value="">Todos os temas</option>{topics.map((t) => <option key={t}>{t}</option>)}</select>}>
          {loading && !data ? <Loading /> : items.length ? (
            <ul className="divide-y divide-slate-100">
              {items.map((i: any) => (
                <li key={i.id} className="py-2 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={i.suspectedInjection ? 'red' : (i.relevance ?? 0) >= 60 ? 'green' : 'slate'}>{i.suspectedInjection ? 'suspeito' : i.relevance ?? '—'}</Badge>
                    <a className="font-medium underline" href={i.url} target="_blank" rel="noreferrer noopener">{i.title}</a>
                  </div>
                  <div className="text-xs text-slate-600">
                    {i.topic ?? '—'} · confiabilidade {i.reliability ?? '—'} · publicado {fmtDate(i.publishedAt)} · coletado {fmtDate(i.retrievedAt)} · <StateBadge state={i.status} />
                  </div>
                  {i.implication && <div className="text-xs">{i.implication}</div>}
                  {i.pautaSuggestion && <div className="text-xs text-brand-800">Pauta candidata: {i.pautaSuggestion}</div>}
                </li>
              ))}
            </ul>
          ) : <Empty>Sem itens. Execute o radar.</Empty>}
        </Card>
        <div className="space-y-4">
          <Card title="Último resumo">{reps.data?.[0] ? <Markdown text={reps.data[0].markdown} /> : <Empty>Sem resumo.</Empty>}</Card>
          <Card title="Fontes autorizadas">
            <ul className="space-y-1 text-xs">
              {data?.sources.map((s: any) => (
                <li key={s.id}>
                  <strong>{s.name}</strong> · {s.domain} · confiança {s.trust}/5 {s.feedUrl ? '· RSS' : '· somente validação de claims'}
                  {s.lastError && <span className="text-red-800"> · erro: {s.lastError}</span>}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </>
  );
}

export function Portfolio() {
  const { can } = useAuth();
  const { data, error, loading, reload } = useApi<any>('/api/offers');
  const act = useAction();
  if (loading && !data) return <Loading />;
  if (!data) return <ErrorBox error={error} />;
  return (
    <>
      <PageHeader title="Portfólio & Distribuidores" subtitle="Ofertas e parcerias são hipóteses até validação. A04 recomenda; comercial/CEO decidem. Nunca “vendemos todo o portfólio”." />
      <ErrorBox error={act.error} />
      <Flash message={act.message} />
      {!data.commercialDataVisible && <p className="mb-3 text-xs text-slate-600">Dados comerciais (preço/margem) ocultos para o seu papel.</p>}
      <div className="space-y-3">
        {data.offers.map((o: any) => (
          <Card key={o.id} title={o.name} actions={<StateBadge state={o.status} />}>
            <div className="grid gap-3 text-sm md:grid-cols-3">
              <div>
                <div className="text-xs text-slate-500">Família · fabricante</div>
                <div>{o.family} · {data.partners.find((p: any) => p.id === o.vendorId)?.name ?? '—'}</div>
                <div className="mt-1 text-xs text-slate-500">Problema resolvido</div>
                <div>{o.problemSolved}</div>
                <div className="mt-1 text-xs text-slate-500">Capacidade de entrega</div>
                <StateBadge state={o.deliveryCapability} />
              </div>
              <div className="md:col-span-2">
                {o.goNoGo ? (
                  <>
                    <Badge tone={o.goNoGo.recommendation === 'GO' ? 'green' : o.goNoGo.recommendation === 'NO_GO' ? 'red' : 'amber'}>A04: {o.goNoGo.recommendation}</Badge>
                    <span className="ml-2 text-xs text-slate-500">{fmtDate(o.goNoGo.evaluatedAt)}</span>
                    <p className="mt-1 text-xs">{o.goNoGo.rationale}</p>
                    <p className="text-xs">Scores: {Object.entries(o.goNoGo.scores).map(([k, v]) => `${k} ${v}/5`).join(' · ')}</p>
                    {o.goNoGo.validationsRequired.length > 0 && <ul className="mt-1 list-disc pl-5 text-xs">{o.goNoGo.validationsRequired.map((v: string) => <li key={v}>{v}</li>)}</ul>}
                  </>
                ) : <p className="text-xs text-slate-500">Ainda não avaliada.</p>}
                <div className="mt-2 flex flex-wrap gap-2">
                  {can('agent:run') && <Button variant="secondary" disabled={act.busy} onClick={() => act.run(() => api(`/api/offers/${o.id}/evaluate`, { method: 'POST' }), 'Avaliação concluída.').then(reload)}>Avaliar com A04</Button>}
                  {can('offer:decide') && (['APROVADA', 'CONDICIONAL', 'NAO_HABILITADA'] as const).map((s) => (
                    <Button key={s} variant={s === 'APROVADA' ? 'success' : s === 'NAO_HABILITADA' ? 'danger' : 'secondary'} disabled={act.busy || o.status === s} onClick={() => {
                      const note = window.prompt(`Justificativa para ${s}:`);
                      if (note && note.length >= 3) void act.run(() => api(`/api/offers/${o.id}/decision`, { method: 'POST', body: { status: s, note } }), `Status: ${s}`).then(reload);
                    }}>{s}</Button>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <Card title="Fabricantes e distribuidores" className="mt-4">
        <Table head={['Nome', 'Tipo', 'Status da parceria', 'Observação']}>
          {data.partners.map((p: any) => (
            <tr key={p.id}>
              <td className="px-3 py-2">{p.name}</td>
              <td className="px-3 py-2">{p.kind}</td>
              <td className="px-3 py-2"><StateBadge state={p.partnershipStatus} /></td>
              <td className="px-3 py-2 text-xs">{p.sourceNote}</td>
            </tr>
          ))}
        </Table>
      </Card>
    </>
  );
}
